import { sha256HexOfText } from "./sha256.js";
// The request SLA policy (CHT-763, Phase 12.3; docs/architecture/milestone-e-jobs-contract.md section 6.1).
//
// Thresholds are OPERATIONAL POLICY in settings, never model judgment and never a hard-coded launch target. No launch SLA exists, so no
// production default exists: a value that is not set (or `off`) switches its stage off, and a family with no value at all is inert. The
// owner chooses the values before production. Every decision of a run carries `policyVersion`, so a staff member can see which policy
// a decision applied.
//
// Everything here is pure: it reads timestamps, states and ids, never a message text.
/** The four stages of the request SLA. A stage is a closed code, and part of the key of its check (`request.sla-check:<id>:v<version>:<stage>`). */
export const requestSlaStages = Object.freeze([
    "UNASSIGNED",
    "OWNER_IDLE",
    "SUPERVISOR_FALLBACK",
    "CUSTOMER_REMIND",
]);
/** The setting that switches the family on (`on`) or off (`off`, the default). The worker reads it. */
export const REQUEST_SLA_FAMILY_VARIABLE = "LAFAYETTE_JOBS_REQUEST_SLA";
/** The minutes variable of each stage. A value is a whole number of minutes from 1 to 10,080, or `off`/unset (the stage is off). */
export const requestSlaVariables = Object.freeze({
    UNASSIGNED: "LAFAYETTE_REQUEST_SLA_UNASSIGNED_MINUTES",
    OWNER_IDLE: "LAFAYETTE_REQUEST_SLA_OWNER_IDLE_MINUTES",
    SUPERVISOR_FALLBACK: "LAFAYETTE_REQUEST_SLA_SUPERVISOR_FALLBACK_MINUTES",
    CUSTOMER_REMIND: "LAFAYETTE_REQUEST_SLA_AWAITING_CUSTOMER_REMIND_MINUTES",
});
export const REQUEST_SLA_MAX_MINUTES = 7 * 24 * 60;
function policyOf(minutes) {
    const normalized = requestSlaStages
        .map((stage) => `${stage}=${minutes[stage] === null ? "off" : minutes[stage]}`)
        .join(";");
    const thresholdsMs = Object.fromEntries(requestSlaStages.map((stage) => {
        const value = minutes[stage];
        return [stage, value === null ? null : value * 60_000];
    }));
    return Object.freeze({
        policyVersion: `request-sla.v1:${sha256HexOfText(normalized).slice(0, 8)}`,
        thresholdsMs: Object.freeze(thresholdsMs),
    });
}
/** The policy with every stage off. The family is inert. */
export const OFF_REQUEST_SLA_POLICY = policyOf({
    UNASSIGNED: null,
    OWNER_IDLE: null,
    SUPERVISOR_FALLBACK: null,
    CUSTOMER_REMIND: null,
});
/** `true` when at least one stage has a threshold. */
export const requestSlaPolicyIsActive = (policy) => requestSlaStages.some((stage) => policy.thresholdsMs[stage] !== null);
function minutesOf(value) {
    const text = (value ?? "").trim().toLowerCase();
    if (text === "" || text === "off")
        return null;
    if (!/^\d{1,5}$/.test(text))
        return "invalid";
    const parsed = Number(text);
    return parsed >= 1 && parsed <= REQUEST_SLA_MAX_MINUTES ? parsed : "invalid";
}
/**
 * Reads the family setting and the four thresholds. Unset, empty and `off` mean off. Anything else that is not a whole number of minutes
 * from 1 to 10,080 is refused with the name of the variable (never the value). The thresholds are read even when the family is `off`, so a
 * typo stops the process before it can be switched on by accident.
 */
export function parseRequestSlaSettings(env) {
    const mode = (env[REQUEST_SLA_FAMILY_VARIABLE] ?? "").trim().toLowerCase();
    if (mode !== "" && mode !== "on" && mode !== "off") {
        return { ok: false, variable: REQUEST_SLA_FAMILY_VARIABLE };
    }
    const minutes = {};
    for (const stage of requestSlaStages) {
        const parsed = minutesOf(env[requestSlaVariables[stage]]);
        if (parsed === "invalid")
            return { ok: false, variable: requestSlaVariables[stage] };
        minutes[stage] = parsed;
    }
    return { ok: true, settings: { mode: mode === "on" ? "on" : "off", policy: policyOf(minutes) } };
}
/** A policy made from whole minutes, for a test or a script that gives the values directly. `null` is off. */
export function requestSlaPolicyOfMinutes(minutes) {
    const full = {};
    for (const stage of requestSlaStages) {
        const value = minutes[stage] ?? null;
        if (value !== null &&
            (!Number.isInteger(value) || value < 1 || value > REQUEST_SLA_MAX_MINUTES)) {
            throw new RangeError("a request SLA threshold is a whole number of minutes from 1 to 10080");
        }
        full[stage] = value;
    }
    return policyOf(full);
}
const notDue = (reason, dueInMs) => dueInMs === undefined ? { due: false, reason } : { due: false, reason, dueInMs };
/**
 * Whether the stage is due for this request NOW. A pure function of the policy, the facts and the clock. The sweep asks it in SQL terms
 * (the same rules), and the handler asks it again after it locked and re-read the request: the decision is the handler's.
 */
export function evaluateRequestSla(policy, stage, facts, now) {
    const threshold = policy.thresholdsMs[stage];
    if (threshold === null)
        return notDue("STAGE_OFF");
    const since = (time) => now.getTime() - time.getTime() >= threshold;
    const remaining = (time) => Math.max(1, threshold - (now.getTime() - time.getTime()));
    switch (stage) {
        case "UNASSIGNED":
            // No owner yet: a SUBMITTED request that nobody was routed to, or whose routed person never claimed it, is the supervisor's to see.
            if (facts.state !== "SUBMITTED" || facts.ownerStaffUserId !== null) {
                return notDue("STATE_CHANGED");
            }
            return since(facts.stateChangedAt)
                ? { due: true }
                : notDue("NOT_YET_DUE", remaining(facts.stateChangedAt));
        case "OWNER_IDLE":
            if (facts.state !== "ASSIGNED" && facts.state !== "IN_PROGRESS") {
                return notDue("STATE_CHANGED");
            }
            // A staff note or a piece of evidence moved the idle time: the check is due again when the new idle time has passed.
            return since(facts.lastStaffActivityAt)
                ? { due: true }
                : notDue("RECENT_ACTIVITY", remaining(facts.lastStaffActivityAt));
        case "SUPERVISOR_FALLBACK": {
            if (facts.state !== "ASSIGNED" && facts.state !== "IN_PROGRESS") {
                return notDue("STATE_CHANGED");
            }
            const escalatedAt = facts.ownerIdleEscalatedAt;
            if (escalatedAt === null)
                return notDue("NOT_ESCALATED");
            // Still open: nobody acted since the owner was told.
            if (facts.lastStaffActivityAt.getTime() > escalatedAt.getTime()) {
                return notDue("RECENT_ACTIVITY");
            }
            return since(escalatedAt) ? { due: true } : notDue("NOT_YET_DUE", remaining(escalatedAt));
        }
        case "CUSTOMER_REMIND":
            if (facts.state !== "AWAITING_CUSTOMER" || facts.awaitingCustomerSince === null) {
                return notDue("STATE_CHANGED");
            }
            return since(facts.awaitingCustomerSince)
                ? { due: true }
                : notDue("NOT_YET_DUE", remaining(facts.awaitingCustomerSince));
    }
}
/**
 * The notice of a due stage. `UNASSIGNED` and `SUPERVISOR_FALLBACK` go to the supervisor queue; `OWNER_IDLE` goes to the owner (the person the
 * request is assigned to, when it has no owner yet; the queue when it has neither); `CUSTOMER_REMIND` goes to the customer. No stage
 * reassigns a request: V1 records and tells, and a person decides.
 */
export function requestSlaNoticeOf(stage, facts) {
    switch (stage) {
        case "UNASSIGNED":
        case "SUPERVISOR_FALLBACK":
            return {
                kind: "REQUEST_SLA_ESCALATED",
                audience: "STAFF",
                staffUserId: null,
                reasonCode: stage,
            };
        case "OWNER_IDLE":
            return {
                kind: "REQUEST_SLA_ESCALATED",
                audience: "STAFF",
                staffUserId: facts.ownerStaffUserId ?? facts.assignedStaffUserId,
                reasonCode: stage,
            };
        case "CUSTOMER_REMIND":
            return {
                kind: "REQUEST_CUSTOMER_REMINDER",
                audience: "CUSTOMER",
                staffUserId: null,
                reasonCode: stage,
            };
    }
}
